import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Calendar } from "lucide-react";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import FaqAccordion from "@/components/seo/FaqAccordion";
import CtaBlock from "@/components/seo/CtaBlock";
import RelatedGrid from "@/components/seo/RelatedGrid";
import useSeo from "@/hooks/useSeo";
import { getPostBySlug, getRelatedPosts } from "@/data/seo/blog";
import { getServiceBySlug } from "@/data/seo/services";
import {
  breadcrumbSchema,
  faqSchema,
  articleSchema,
  canonicalFor,
} from "@/lib/seoHelpers";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  const canonical = canonicalFor(`/blog/${slug}`);
  const crumbs = post
    ? [
        { label: "Home", href: "/" },
        { label: "Blog", href: "/blog" },
        { label: post.category, href: "/blog" },
        { label: post.title, href: `/blog/${slug}` },
      ]
    : [];

  useSeo({
    title: post?.metaTitle,
    description: post?.metaDescription,
    canonical,
    ogImage: post?.heroImage,
  });

  if (!post) return <Navigate to="/404" replace />;

  const schemas = [
    breadcrumbSchema(crumbs),
    faqSchema(post.faqs),
    articleSchema({
      title: post.title,
      description: post.metaDescription,
      slug: post.slug,
      image: post.heroImage,
      datePublished: post.datePublished,
      dateModified: post.dateModified,
    }),
  ];

  const relatedPosts = getRelatedPosts(slug).map((p) => ({
    href: `/blog/${p.slug}`,
    label: p.title,
    eyebrow: p.category,
    description: p.excerpt,
  }));

  const relatedServices = (post.relatedServices || [])
    .map((s) => getServiceBySlug(s))
    .filter(Boolean)
    .map((s) => ({
      href: `/services/${s.slug}`,
      label: s.title,
      eyebrow: s.eyebrow,
      description: s.metaDescription.slice(0, 120) + "…",
    }));

  return (
    <SeoPageLayout schemas={schemas}>
      <article
        data-testid={`blog-post-${slug}`}
        className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-8">
          <span className="overline text-[var(--iw-gold)]">
            {post.category}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-5xl lg:text-6xl leading-[1.05] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {post.title}
          </motion.h1>
          <div className="flex flex-wrap items-center gap-5 mt-6 overline text-[var(--iw-faint)]">
            <span className="flex items-center gap-2">
              <Calendar size={11} className="text-[var(--iw-gold)]" />
              {new Date(post.datePublished).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={11} className="text-[var(--iw-gold)]" />
              {post.readTime}
            </span>
          </div>
        </div>

        <div
          className="relative aspect-[16/9] mb-10 overflow-hidden"
          style={{ border: "1px solid rgba(212,175,55,0.2)" }}
        >
          <img
            src={post.heroImage}
            alt={`${post.title} — InvitationWala blog cover`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        <p className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mb-12 font-serif-display italic"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {post.excerpt}
        </p>

        <div className="space-y-12">
          {post.sections.map((sec, i) => (
            <section key={i} data-testid={`blog-section-${i}`}>
              <h2
                className="font-serif-display text-[var(--iw-ivory)] text-2xl md:text-3xl leading-tight mb-4"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {sec.h2}
              </h2>
              {sec.paragraphs?.map((p, pi) => (
                <p
                  key={pi}
                  className="text-[var(--iw-muted)] leading-relaxed text-base md:text-lg mb-4"
                >
                  {p}
                </p>
              ))}
              {sec.bullets && (
                <ul className="mt-4 space-y-2.5">
                  {sec.bullets.map((b, bi) => (
                    <li
                      key={bi}
                      className="flex items-start gap-3 text-[var(--iw-muted)] leading-relaxed"
                    >
                      <span className="text-[var(--iw-gold)] mt-1 shrink-0">
                        ✦
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <section className="mt-16" data-testid="blog-faq-section">
          <h2
            className="font-serif-display text-[var(--iw-ivory)] text-2xl md:text-3xl leading-tight mb-6"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Frequently Asked Questions
          </h2>
          <FaqAccordion faqs={post.faqs} testid={`faq-blog-${slug}`} />
        </section>

        <CtaBlock
          title="Ready to design your wedding invitation?"
          subtitle="Get a transparent quote and reference reel in under an hour on WhatsApp."
          prefilledMessage={`Hi InvitationWala, I read your article on ${post.title} and I'd like to enquire about a wedding invitation.`}
          testid={`cta-blog-${slug}`}
        />

        {relatedServices.length > 0 && (
          <RelatedGrid
            title="Related Services"
            items={relatedServices}
            testid="blog-related-services"
          />
        )}

        {relatedPosts.length > 0 && (
          <RelatedGrid
            title="Related Reading"
            items={relatedPosts}
            testid="blog-related-posts"
          />
        )}
      </article>
    </SeoPageLayout>
  );
}
